'use client';

import { pushToDataLayer } from 'components/shared/UserAnalytics';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { CustomImage } from '../..';
import styles from './RingSizeGuideModal.module.css';

const cx = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

type Tab = 'measure' | 'chart' | 'specs';

const CA_SIZE_CHART = [
  {
    caSize: '5*',
    caId: '15.7',
    airSize: '5',
    airCirc: '51.3',
    airId: '16.3',
    warnRow: true,
  },
  {
    caSize: '5.5',
    caId: '16.1',
    airSize: '5',
    airCirc: '51.3',
    airId: '16.3',
    warnRow: false,
  },
  {
    caSize: '6',
    caId: '16.5',
    airSize: '5',
    airCirc: '51.3',
    airId: '16.3',
    warnRow: false,
  },
  {
    caSize: '6.5',
    caId: '17.0',
    airSize: '6',
    airCirc: '54.0',
    airId: '17.2',
    warnRow: false,
  },
  {
    caSize: '7',
    caId: '17.4',
    airSize: '6',
    airCirc: '54.0',
    airId: '17.2',
    warnRow: false,
  },
  {
    caSize: '7.5*',
    caId: '17.8',
    airSize: '7',
    airCirc: '57.3',
    airId: '18.2',
    warnRow: true,
  },
  {
    caSize: '8',
    caId: '18.2',
    airSize: '7',
    airCirc: '57.3',
    airId: '18.2',
    warnRow: false,
  },
  {
    caSize: '8.5*',
    caId: '18.6',
    airSize: '7',
    airCirc: '57.3',
    airId: '18.2',
    warnRow: true,
  },
  {
    caSize: '9',
    caId: '18.9',
    airSize: '8',
    airCirc: '60.2',
    airId: '19.2',
    warnRow: false,
  },
  {
    caSize: '9.5',
    caId: '19.4',
    airSize: '8',
    airCirc: '60.2',
    airId: '19.2',
    warnRow: false,
  },
  {
    caSize: '10',
    caId: '19.8',
    airSize: '9',
    airCirc: '63.2',
    airId: '20.1',
    warnRow: false,
  },
  {
    caSize: '10.5',
    caId: '20.3',
    airSize: '9',
    airCirc: '63.2',
    airId: '20.1',
    warnRow: false,
  },
  {
    caSize: '11',
    caId: '20.7',
    airSize: '10',
    airCirc: '65.7',
    airId: '20.9',
    warnRow: false,
  },
  {
    caSize: '11.5',
    caId: '21.1',
    airSize: '10',
    airCirc: '65.7',
    airId: '20.9',
    warnRow: false,
  },
  {
    caSize: '12',
    caId: '21.5',
    airSize: '11',
    airCirc: '67.9',
    airId: '21.6',
    warnRow: false,
  },
  {
    caSize: '12.5',
    caId: '21.9',
    airSize: '11',
    airCirc: '67.9',
    airId: '21.6',
    warnRow: false,
  },
  {
    caSize: '13',
    caId: '22.3',
    airSize: '12',
    airCirc: '70.5',
    airId: '22.5',
    warnRow: false,
  },
  {
    caSize: '13.5',
    caId: '22.7',
    airSize: '12',
    airCirc: '70.5',
    airId: '22.5',
    warnRow: false,
  },
  {
    caSize: '14*',
    caId: '23.0',
    airSize: '12',
    airCirc: '70.5',
    airId: '22.5',
    warnRow: true,
  },
];

const FULL_SPECS_DATA = [
  { size: 5, circ: '51.3', id: '16.3', od: '21.3', hrs: '3.6' },
  { size: 6, circ: '54.0', id: '17.2', od: '22.5', hrs: '3.8' },
  { size: 7, circ: '57.3', id: '18.2', od: '23.4', hrs: '3.7' },
  { size: 8, circ: '60.2', id: '19.2', od: '24.4', hrs: '3.7' },
  { size: 9, circ: '63.2', id: '20.1', od: '25.2', hrs: '3.9' },
  { size: 10, circ: '65.7', id: '20.9', od: '26.1', hrs: '4.0' },
  { size: 11, circ: '67.9', id: '21.6', od: '26.8', hrs: '3.9' },
  { size: 12, circ: '70.5', id: '22.5', od: '27.9', hrs: '4.2' },
  { size: 13, circ: '76.0', id: '24.2', od: '29.3', hrs: '4.1' },
  { size: 14, circ: '78.8', id: '25.1', od: '30.1', hrs: '4.2' },
];

export const RingSizeGuideModal = ({ className }: { className?: string }) => {
  const t = useTranslations('ring');
  const [activeTab, setActiveTab] = useState<Tab>('measure');

  const handleTabClick = (tab: Tab) => {
    pushToDataLayer('ringSizeGuideTabClick', { tab });
    setActiveTab(tab);
  };

  return (
    <div
      className={cx(styles.root, className)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      role="button"
      tabIndex={0}
    >
      {/* ── Header ── */}
      <div className="modal-head">
        <div>
          <div className="modal-title">
            {t('ring.productSelection.sizingSection.sizeGuideCA.title')}
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="tab-row">
        <button
          className={activeTab === 'measure' ? 'tab-btn active' : 'tab-btn'}
          onClick={() => handleTabClick('measure')}
        >
          {t('ring.productSelection.sizingSection.sizeGuideCA.tabs.measure')}
        </button>
        <button
          className={activeTab === 'specs' ? 'tab-btn active' : 'tab-btn'}
          onClick={() => handleTabClick('specs')}
        >
          {t('ring.productSelection.sizingSection.sizeGuideCA.tabs.specs')}
        </button>
        <button
          className={activeTab === 'chart' ? 'tab-btn active' : 'tab-btn'}
          onClick={() => handleTabClick('chart')}
        >
          {t('ring.productSelection.sizingSection.sizeGuideCA.tabs.chart')}
        </button>
      </div>

      {/* ── How to Measure ── */}
      {activeTab === 'measure' && (
        <div>
          <div className="measure-steps-grid">
            <div className="measure-step-wrap">
              <CustomImage
                src="web_v2/ring-buy/sizing-guides/ring-measure-step-1.png"
                alt={t(
                  'ring.productSelection.sizingSection.sizeGuideCA.measure.stepAlt',
                  { index: 1 },
                )}
                fill
                sizes="(max-width: 480px) 100vw, 33vw"
                style={{ objectFit: 'cover' }}
              />
            </div>
            <div className="measure-step-wrap">
              <CustomImage
                src="web_v2/ring-buy/sizing-guides/ring-measure-step-2.png"
                alt={t(
                  'ring.productSelection.sizingSection.sizeGuideCA.measure.stepAlt',
                  { index: 2 },
                )}
                fill
                sizes="(max-width: 480px) 100vw, 33vw"
                style={{ objectFit: 'cover' }}
              />
            </div>
            <div className="measure-step-wrap">
              <CustomImage
                src="web_v2/ring-buy/sizing-guides/ring-measure-step-3.png"
                alt={t(
                  'ring.productSelection.sizingSection.sizeGuideCA.measure.stepAlt',
                  { index: 3 },
                )}
                fill
                sizes="(max-width: 480px) 100vw, 33vw"
                style={{ objectFit: 'cover' }}
              />
            </div>
            <div className="measure-step-wrap">
              <CustomImage
                src="web_v2/ring-buy/sizing-guides/ring-measure-step-4.png"
                alt={t(
                  'ring.productSelection.sizingSection.sizeGuideCA.measure.stepAlt',
                  { index: 4 },
                )}
                fill
                sizes="(max-width: 480px) 100vw, 33vw"
                style={{ objectFit: 'cover' }}
              />
            </div>
          </div>
          <div className="tip">
            <strong>
              {t(
                'ring.productSelection.sizingSection.sizeGuideCA.measure.tipLabel',
              )}
            </strong>{' '}
            {t(
              'ring.productSelection.sizingSection.sizeGuideCA.measure.tipText',
            )}
          </div>
        </div>
      )}

      {/* ── Size Chart ── */}
      {activeTab === 'chart' && (
        <div>
          <div className="sec-lbl">
            {t(
              'ring.productSelection.sizingSection.sizeGuideCA.chart.sectionLabel',
            )}
          </div>
          <div className="table-wrap">
            <table className="chart-table">
              <thead>
                <tr>
                  <th colSpan={2} className="group-header canada-header">
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.chart.canadaHeader',
                    )}
                  </th>
                  <th colSpan={2} className="group-header ring-air-header">
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.chart.ringAirHeader',
                    )}
                  </th>
                </tr>
                <tr>
                  <th>
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.chart.sizeColumn',
                    )}
                  </th>
                  <th>
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.chart.idColumn',
                    )}
                  </th>
                  <th>
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.chart.sizeColumn',
                    )}
                  </th>
                  <th>
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.chart.idColumn',
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {CA_SIZE_CHART.map(
                  ({ caSize, caId, airSize, airId, warnRow }) => (
                    <tr
                      key={caSize}
                      className={`row${warnRow ? ' warn-row' : ''}`}
                    >
                      <td className="bold">{caSize}</td>
                      <td>{caId}</td>
                      <td className="bold">{airSize}</td>
                      <td>{airId}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
          <div className="note">
            *{' '}
            <strong>
              {t(
                'ring.productSelection.sizingSection.sizeGuideCA.chart.noteStrong',
              )}
            </strong>{' '}
            {t(
              'ring.productSelection.sizingSection.sizeGuideCA.chart.noteRest',
            )}
          </div>
          <div className="chart-legend">
            <strong>
              {t(
                'ring.productSelection.sizingSection.sizeGuideCA.chart.idLegendLabel',
              )}
            </strong>{' '}
            {t(
              'ring.productSelection.sizingSection.sizeGuideCA.chart.idLegendText',
            )}
          </div>
          <div className="kit-banner">
            <span style={{ fontSize: '22px' }}>📦</span>
            <div className="kit-text">
              <strong>
                {t(
                  'ring.productSelection.sizingSection.sizeGuideCA.kitBanner.title',
                )}
              </strong>
              <br />
              {t(
                'ring.productSelection.sizingSection.sizeGuideCA.kitBanner.subtitle',
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Full Specs ── */}
      {activeTab === 'specs' && (
        <div>
          <div className="sec-lbl">
            {t(
              'ring.productSelection.sizingSection.sizeGuideCA.specs.sectionLabel',
            )}
          </div>
          <div className="table-wrap">
            <table className="specs-table">
              <thead>
                <tr>
                  <th>
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.sizeColumn',
                    )}
                  </th>
                  <th>
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.circumferenceColumn',
                    )}
                  </th>
                  <th>
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.idColumn',
                    )}
                  </th>
                  <th>
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.odColumn',
                    )}
                  </th>
                  <th>
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.hrsColumn',
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {FULL_SPECS_DATA.map(({ size, circ, id, od, hrs }) => (
                  <tr key={size} className="row">
                    <td className="bold">{size}</td>
                    <td>{circ}</td>
                    <td>{id}</td>
                    <td>{od}</td>
                    <td>{hrs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="specs-width-note">
            {t(
              'ring.productSelection.sizingSection.sizeGuideCA.specs.widthNote',
            )}
          </div>
          <div className="specs-diagram-row">
            <CustomImage
              src="web_v2/ring-buy/sizing-guides/ring-measurement-diagram.png"
              alt={t(
                'ring.productSelection.sizingSection.sizeGuideCA.specs.diagramAlt',
              )}
              width={400}
              height={400}
              className="specs-diagram-img"
            />
            <ul className="specs-legend">
              <li>
                <span className="specs-dot" />
                <div>
                  <div className="specs-leg-title">
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.legendIdTitle',
                    )}
                  </div>
                  <div className="specs-leg-sub">
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.legendIdSub',
                    )}
                  </div>
                </div>
              </li>
              <li>
                <span className="specs-dot" />
                <div>
                  <div className="specs-leg-title">
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.legendOdTitle',
                    )}
                  </div>
                  <div className="specs-leg-sub">
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.legendOdSub',
                    )}
                  </div>
                </div>
              </li>
              <li>
                <span className="specs-dot" />
                <div>
                  <div className="specs-leg-title">
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.legendHrsTitle',
                    )}
                  </div>
                  <div className="specs-leg-sub">
                    {t(
                      'ring.productSelection.sizingSection.sizeGuideCA.specs.legendHrsSub',
                    )}
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div className="kit-banner">
            <span style={{ fontSize: '22px' }}>📦</span>
            <div className="kit-text">
              <strong>
                {t(
                  'ring.productSelection.sizingSection.sizeGuideCA.kitBanner.title',
                )}
              </strong>
              <br />
              {t(
                'ring.productSelection.sizingSection.sizeGuideCA.kitBanner.subtitle',
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
